import SelectSheet from '@/components/base/SelectSheet';
import {
  getGenerationEntryConfig,
  getModelQualityPointsCost,
  modelSupportsGenerationEntry,
} from '@/api/config';
import { useAppConfig, refreshModelsConfig } from '@/hooks/useAppConfig';

interface ModelSelectorProps {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  isOpen: boolean;
  onClose: () => void;
  quality?: string;
  mode?: string;
  referenceImageCount?: number;
}

export default function ModelSelector({
  value,
  onChange,
  disabled,
  isOpen,
  onClose,
  quality,
  mode = 'text2img',
  referenceImageCount = 0,
}: ModelSelectorProps) {
  const config = useAppConfig();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      refreshModelsConfig();
    }
  }, [isOpen]);

  const entry = getGenerationEntryConfig(config, mode);
  const models = (config?.models ?? []).filter((model) => (
    modelSupportsGenerationEntry(model, entry, referenceImageCount)
  ));

  const getModelCost = (modelId: string): number | null => {
    const model = models.find((item) => item.id === modelId);
    const supported = model?.supported_qualities ?? [];
    const requested = quality ?? 'standard';
    const q = supported.includes(requested)
      ? requested
      : supported.includes('standard')
        ? 'standard'
        : supported[0];
    if (!q) return null;
    return getModelQualityPointsCost(config, modelId, q);
  };

  const options = models.map((model) => {
    const cost = getModelCost(model.id);
    const badge = model.is_recommended
      ? t('gen_recommended')
      : model.is_new
        ? 'NEW'
        : undefined;
    const desc = model.description_zh ?? model.description ?? '';
    const description = cost !== null
      ? `${desc} · ${t('gen_model_cost', { cost })}`
      : desc;

    return {
      id: model.id,
      label: model.name_zh ?? model.name,
      icon: model.icon ?? undefined,
      description,
      badge,
      disabled,
    };
  });

  return (
    <SelectSheet
      title={t('gen_modelLabel')}
      options={options}
      selectedId={value}
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(id) => {
        onChange(id);
        onClose();
      }}
    />
  );
}
